<template>
  <!-- STOCK → FSC : récapitulatif annuel des volumes (Anexa 10) pour l'audit FSC, généré depuis
       l'ERP en PDF et Word. Matière première = réceptions RNP Romsilva ; produits = factures dont
       le devis porte « FSC Required » ; groupes = dictionnaire Anexa 4 (modifiable en bas). -->
  <div id="page-fsc-summary">
    <vx-card class="mb-base">
      <div class="fsc-head">
        <div>
          <h4>FSC — annual volume summary <span class="fsc-help" title="How it works" @click="helpPopup = true">?</span></h4>
          <span class="fsc-sub">Anexa 10 “Sumarul anual al volumelor”, built from Romsilva receptions and FSC invoices</span>
        </div>
      </div>

      <div class="fsc-toolbar mt-4">
        <div class="fsc-toolbar__fields">
          <vs-input type="date" label="Start date" v-model="from" class="mr-4" />
          <vs-input type="date" label="End date" v-model="to" class="mr-4" />
          <vs-input type="number" step="0.01" min="0" label="Estimated current FSC stock (m³)" v-model="stock" class="fsc-stock" />
        </div>
        <div class="fsc-toolbar__actions">
          <vs-button icon-pack="feather" icon="icon-play" :disabled="loading" @click="generate">Generate</vs-button>
          <vs-button v-if="result" class="ml-2" color="danger" type="border" icon-pack="feather" icon="icon-file-text"
            :disabled="loading" @click="download('pdf')">Download PDF</vs-button>
          <vs-button v-if="result" class="ml-2" color="primary" type="border" icon-pack="feather" icon="icon-file"
            :disabled="loading" @click="download('docx')">Download Word</vs-button>
          <vs-button v-if="result" class="ml-2" color="success" type="border" icon-pack="feather" icon="icon-archive"
            :disabled="loading" @click="download('zip')">FSC invoices (ZIP)</vs-button>
        </div>
      </div>

      <!-- Filtre d'exclusion : vaut pour le tableau ET pour l'export des factures -->
      <div class="fsc-filters mt-4">
        <span class="fsc-filters__label">Exclude</span>
        <vs-checkbox v-model="exclude.eucalyptus">Eucalyptus</vs-checkbox>
        <vs-checkbox v-model="exclude.finger_jointed" class="ml-2">Finger-jointed</vs-checkbox>
        <v-select multiple append-to-body class="fsc-filters__cats ml-2" :options="categoryOptions"
          :reduce="o => o.value" label="label" v-model="excludeCats" placeholder="Other categories to exclude" />
      </div>
      <p class="fsc-note mt-1">An invoice with one excluded line is left out entirely, from the Anexa 10 table and from the invoices export.</p>

      <div v-if="result && stale" class="fsc-stale mt-3">
        <feather-icon icon="AlertCircleIcon" svgClasses="h-4 w-4" class="mr-1" />
        The dates, the stock or the exclusions changed since the last generation: click Generate again before downloading.
      </div>

      <vs-alert v-if="error" color="danger" icon-pack="feather" icon="icon-alert-circle" :active="true" class="mt-4">
        {{ error }}
      </vs-alert>
    </vx-card>

    <template v-if="result">
      <!-- Aperçu : le tableau tel qu'il sera exporté -->
      <vx-card title="Anexa 10 — preview" :subtitle="`perioada: ${dayRo(result.from)} - ${dayRo(result.to)}`" class="mb-base">
        <div class="fsc-anexa-wrap">
          <table class="fsc-anexa">
            <thead>
              <tr>
                <th>Type and category</th>
                <th>Raw Material Purchased<br><small>(volume or weight)</small></th>
                <th>Raw Material Used<br><small>(volume or weight)</small></th>
                <th>Raw Material In Stock<br><small>(volume or weight)</small></th>
                <th>Product group produced<br><small>(Type and category)</small></th>
                <th>Conversion Factor</th>
                <th>Products Produced<br><small>(volume or weight)</small></th>
                <th>Products Sold<br><small>(volume or weight)</small></th>
                <th>Finished Material On Hand<br><small>(volume or weight)</small></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in previewRows" :key="i">
                <template v-if="i === 0">
                  <td :rowspan="previewRows.length">{{ result.raw.label }}<br>{{ result.raw.claim }}</td>
                  <td :rowspan="previewRows.length">{{ m3(result.raw.purchased) }}</td>
                  <td :rowspan="previewRows.length">{{ m3(result.raw.used) }}</td>
                  <td :rowspan="previewRows.length">{{ m3(result.raw.stock) }}</td>
                </template>
                <td>{{ r.label }}<br><span :class="{ 'fsc-nonfsc': r.fsc === false }">{{ r.claim }}</span></td>
                <td>{{ r.conversion_factor === '' ? '' : Number(r.conversion_factor).toFixed(2) }}</td>
                <td>{{ r.produced === '' ? '' : m3(r.produced) }}</td>
                <td>{{ r.sold === '' ? '' : m3(r.sold) }}</td>
                <td>{{ r.on_hand === '' ? '' : (Number(r.on_hand) === 0 ? '0' : m3(r.on_hand)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </vx-card>

      <!-- Contrôle d'équilibre : acheté = utilisé + stock -->
      <vx-card title="Balance check" subtitle="Purchased should equal Used + In stock" class="mb-base">
        <div class="fsc-balance">
          <div class="fsc-balance__item">
            <span class="fsc-balance__label">Purchased (Romsilva)</span>
            <span class="fsc-balance__value">{{ fmt(result.raw.purchased) }} m³</span>
          </div>
          <span class="fsc-balance__op">=</span>
          <div class="fsc-balance__item">
            <span class="fsc-balance__label">Used (sold ÷ factor)</span>
            <span class="fsc-balance__value">{{ fmt(result.raw.used) }} m³</span>
          </div>
          <span class="fsc-balance__op">+</span>
          <div class="fsc-balance__item">
            <span class="fsc-balance__label">In stock (your estimate)</span>
            <span class="fsc-balance__value">{{ fmt(result.raw.stock) }} m³</span>
          </div>
          <div class="fsc-balance__item" :class="balanced ? 'is-ok' : 'is-gap'">
            <span class="fsc-balance__label">Gap</span>
            <span class="fsc-balance__value">{{ balanced ? '0.00' : fmt(result.raw.gap) }} m³</span>
          </div>
        </div>
        <p v-if="!balanced" class="fsc-note mt-3">
          The table does not balance. Stock that would balance: <b>{{ fmt(result.raw.balancing_stock) }} m³</b>
          (purchased − used).
          <span v-if="result.unmapped.length || result.missingVolume.length">
            Check first the {{ result.unmapped.length ? 'unmapped categories' : '' }}{{ result.unmapped.length && result.missingVolume.length ? ' and the ' : '' }}{{ result.missingVolume.length ? 'lines without volume' : '' }} below.
          </span>
          <a href="#" @click.prevent="helpPopup = true">Why can there be a gap?</a>
        </p>
      </vx-card>

      <!-- PDF des factures FSC : tous présents et à jour avant l'export ZIP -->
      <vx-card id="fsc-pdf-card" title="Invoice PDFs" subtitle="Every FSC invoice must have an up-to-date PDF, with the FSC mention, before the ZIP export" class="mb-base">
        <div class="fsc-pdf-actions">
          <vs-button type="border" icon-pack="feather" icon="icon-check-square" :disabled="loading || pdfChecking || pdfRun.running"
            @click="checkPdfs">{{ pdfCheck ? 'Check again' : 'Check the PDFs' }}</vs-button>
          <vs-button v-if="pdfToFix.length && !pdfRun.running" class="ml-2" color="warning" icon-pack="feather" icon="icon-refresh-cw"
            :disabled="loading || pdfChecking" @click="generatePdfs">Generate the {{ pdfToFix.length }} PDF{{ pdfToFix.length === 1 ? '' : 's' }}</vs-button>
          <vs-button v-if="pdfRun.running" class="ml-2" color="danger" type="border" icon-pack="feather" icon="icon-square"
            @click="pdfRun.cancelled = true">Stop</vs-button>
          <span v-if="pdfChecking" class="fsc-note ml-3">Checking every invoice PDF…</span>
        </div>

        <div v-if="pdfRun.running" class="mt-4">
          <div class="fsc-note mb-1">Generating {{ pdfRun.done + 1 }} / {{ pdfRun.total }} — {{ pdfRun.current }}</div>
          <vs-progress :percent="pdfRun.total ? Math.round(100 * pdfRun.done / pdfRun.total) : 0" color="warning" />
        </div>

        <div v-if="pdfCheck" class="fsc-pdf-summary mt-4">
          <vs-chip :color="pdfCheck.allOk ? 'success' : 'danger'">
            {{ pdfCheck.allOk ? `All ${pdfCheck.invoices.length} PDFs are up to date` : `${pdfToFix.length} of ${pdfCheck.invoices.length} PDFs to generate` }}
          </vs-chip>
          <vs-chip v-for="c in pdfProblemCounts" :key="c.status" color="warning">{{ c.label }}: {{ c.n }}</vs-chip>
        </div>

        <vs-table v-if="pdfCheck && pdfToFix.length" :data="pdfToFix" :max-items="10" pagination class="mt-4">
          <template slot="thead">
            <vs-th>Invoice</vs-th>
            <vs-th>Date</vs-th>
            <vs-th>Client</vs-th>
            <vs-th>Status</vs-th>
            <vs-th>Last generation</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="p in data" :key="p.idoperation">
              <vs-td>
                <a :href="$router.resolve({ path: `/invoice/${p.idoperation}` }).href" target="_blank">{{ p.invoice_no || ('#' + p.idoperation) }}</a>
              </vs-td>
              <vs-td>{{ p.invoice_date }}</vs-td>
              <vs-td>{{ p.client }}</vs-td>
              <vs-td>{{ p.label }}<span v-if="p.detail" class="fsc-muted"> ({{ p.detail }})</span></vs-td>
              <vs-td><span v-if="pdfFailures[p.idoperation]" class="fsc-nonfsc">{{ pdfFailures[p.idoperation] }}</span></vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>

      <!-- Factures écartées par le filtre -->
      <vx-card v-if="result.excludedFsc.length" title="Invoices left out by the filter"
        :subtitle="excludedSubtitle" class="mb-base">
        <vs-table :data="result.excludedFsc" :max-items="10" pagination>
          <template slot="thead">
            <vs-th>Invoice</vs-th>
            <vs-th>Date</vs-th>
            <vs-th>Client</vs-th>
            <vs-th>Reason</vs-th>
            <vs-th>Volume (m³)</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="e in data" :key="e.idoperation">
              <vs-td>
                <a :href="$router.resolve({ path: `/invoice/${e.idoperation}` }).href" target="_blank">{{ e.invoice_no || ('#' + e.idoperation) }}</a>
              </vs-td>
              <vs-td>{{ e.invoice_date }}</vs-td>
              <vs-td>{{ e.client }}</vs-td>
              <vs-td>{{ e.reasons }}</vs-td>
              <vs-td class="fsc-num">{{ fmt(e.m3) }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>

      <!-- À corriger : catégories non rangées, lignes sans volume -->
      <vx-card v-if="result.unmapped.length" title="Unmapped categories" subtitle="Sold on FSC invoices but in no product group: not counted. Pick a group (or “Not FSC goods”) and the report is generated again." class="mb-base fsc-warn-card">
        <vs-table :data="result.unmapped">
          <template slot="thead">
            <vs-th>Category</vs-th>
            <vs-th>Lines</vs-th>
            <vs-th>Volume (m³)</vs-th>
            <vs-th>Product group</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="u in data" :key="u.idcategory">
              <vs-td>{{ u.category || ('#' + u.idcategory) }}</vs-td>
              <vs-td>{{ u.lines }}</vs-td>
              <vs-td class="fsc-num">{{ fmt(u.m3) }}</vs-td>
              <vs-td class="fsc-select-cell">
                <v-select append-to-body :options="groupOptions" :reduce="o => o.value" label="label" :clearable="false"
                  placeholder="Choose a group" :value="null"
                  @input="v => saveMap({ idproductcategory: u.idcategory }, v, true)" />
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>

      <vx-card v-if="result.missingVolume.length" title="Lines without volume" subtitle="The product has no volume and its unit is not m³: counted as 0. Fix the product volume, then generate again." class="mb-base fsc-warn-card">
        <vs-table :data="result.missingVolume" :max-items="10" pagination>
          <template slot="thead">
            <vs-th>Invoice</vs-th>
            <vs-th>Date</vs-th>
            <vs-th>Product</vs-th>
            <vs-th>Quantity</vs-th>
            <vs-th>Unit</vs-th>
            <vs-th>Group</vs-th>
            <vs-th>Claim</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="(m, i) in data" :key="i">
              <vs-td>
                <a :href="$router.resolve({ path: `/invoice/${m.idoperation}` }).href" target="_blank">{{ m.invoice_no || ('#' + m.idoperation) }}</a>
              </vs-td>
              <vs-td>{{ m.invoice_date }}</vs-td>
              <vs-td>{{ m.product }}</vs-td>
              <vs-td class="fsc-num">{{ fmt(m.qty) }}</vs-td>
              <vs-td>{{ m.uom }}</vs-td>
              <vs-td>{{ groupLabel(m.group_code) }}</vs-td>
              <vs-td>{{ m.fsc ? 'FSC 100%' : 'Non FSC' }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>

      <!-- Détail des sources -->
      <vx-card title="Romsilva receptions" :subtitle="`Raw material purchased: ${fmt(result.raw.purchased)} m³`" class="mb-base">
        <vs-table :data="result.purchases.suppliers" noDataText="No reception from RNP Romsilva in this period.">
          <template slot="thead">
            <vs-th>Supplier</vs-th>
            <vs-th>Receptions</vs-th>
            <vs-th>Standing wood (m³)</vs-th>
            <vs-th>Delivered wood (m³)</vs-th>
            <vs-th>Total (m³)</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="s in data" :key="s.idperson">
              <vs-td>{{ s.supplier }}</vs-td>
              <vs-td>{{ s.receptions }}</vs-td>
              <vs-td class="fsc-num">{{ fmt(s.standing_m3) }}</vs-td>
              <vs-td class="fsc-num">{{ fmt(s.delivered_m3) }}</vs-td>
              <vs-td class="fsc-num"><b>{{ fmt(s.m3) }}</b></vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <div v-if="result.purchases.otherDistricts.length" class="mt-6">
          <h6 class="mb-1">Other forest districts — not counted</h6>
          <p class="fsc-note mb-2">Communal, private or unrecognised forest districts. If one of them is in fact a Romsilva district, its contact needs the Romsilva tax ID (1590120).</p>
          <vs-table :data="result.purchases.otherDistricts">
            <template slot="thead">
              <vs-th>Supplier</vs-th>
              <vs-th>Receptions</vs-th>
              <vs-th>Volume (m³)</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr v-for="s in data" :key="s.idperson">
                <vs-td>{{ s.supplier }}</vs-td>
                <vs-td>{{ s.receptions }}</vs-td>
                <vs-td class="fsc-num">{{ fmt(s.m3) }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </div>

        <div v-if="result.purchases.notInM3.length" class="mt-6">
          <h6 class="mb-1">Romsilva reception lines not in m³ — not counted</h6>
          <vs-table :data="result.purchases.notInM3">
            <template slot="thead">
              <vs-th>Reception</vs-th>
              <vs-th>Supplier</vs-th>
              <vs-th>Product</vs-th>
              <vs-th>Quantity</vs-th>
              <vs-th>Unit</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr v-for="(l, i) in data" :key="i">
                <vs-td><a :href="$router.resolve({ path: `/reception/${l.idoperation}` }).href" target="_blank">#{{ l.idoperation }}</a></vs-td>
                <vs-td>{{ l.supplier }}</vs-td>
                <vs-td>{{ l.product }}</vs-td>
                <vs-td class="fsc-num">{{ fmt(l.qty) }}</vs-td>
                <vs-td>{{ l.uom }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </div>
      </vx-card>

      <vx-card title="Sales by product group" subtitle="Where each group's volume comes from (category, or product when it overrides its category)" class="mb-base">
        <vs-table :data="result.salesBySource" noDataText="No FSC sale in this period.">
          <template slot="thead">
            <vs-th>Product group</vs-th>
            <vs-th>Claim</vs-th>
            <vs-th>Category / product</vs-th>
            <vs-th>Lines</vs-th>
            <vs-th>Volume (m³)</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="(s, i) in data" :key="i">
              <vs-td>{{ s.group_label }}</vs-td>
              <vs-td><span :class="{ 'fsc-nonfsc': !s.fsc }">{{ s.fsc ? 'FSC 100%' : 'Non FSC' }}</span></vs-td>
              <vs-td>{{ s.source }}</vs-td>
              <vs-td>{{ s.lines }}</vs-td>
              <vs-td class="fsc-num">{{ fmt(s.m3) }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>
    </template>

    <!-- Dictionnaire Anexa 4 : coefficients et rattachement des catégories -->
    <vx-card class="mb-base">
      <div class="fsc-dict-head" @click="toggleDictionary">
        <div>
          <h4 class="mb-1">Product groups (Anexa 4)</h4>
          <span class="fsc-sub">Conversion factors and which product category belongs to which group. Changes apply to the next generation.</span>
        </div>
        <feather-icon :icon="dictOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" svgClasses="h-5 w-5" />
      </div>

      <div v-if="dictOpen" class="mt-4">
        <vs-alert v-if="dictError" color="danger" :active="true" class="mb-4">{{ dictError }}</vs-alert>

        <h6 class="mb-2">Groups</h6>
        <vs-table :data="dictionary.groups" class="mb-6">
          <template slot="thead">
            <vs-th>Code</vs-th>
            <vs-th>Label (as printed)</vs-th>
            <vs-th>Conversion factor</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="g in data" :key="g.code">
              <vs-td>{{ g.code }}</vs-td>
              <vs-td>{{ g.label }}</vs-td>
              <vs-td>
                <vs-input type="number" step="0.01" min="0.01" max="1" class="fsc-factor"
                  :value="g.conversion_factor" @change="v => saveFactor(g, v)" />
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <div class="fsc-dict-filter mb-2">
          <h6>Product categories</h6>
          <vs-input v-model="catFilter" placeholder="Filter categories" icon-pack="feather" icon="icon-search" class="ml-4" />
          <vs-checkbox v-model="showInactive" class="ml-4">Show inactive</vs-checkbox>
        </div>
        <vs-table :data="filteredCategories" :max-items="15" pagination class="mb-6">
          <template slot="thead">
            <vs-th>#</vs-th>
            <vs-th>Category</vs-th>
            <vs-th>Product group</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="c in data" :key="c.id">
              <vs-td>{{ c.id }}</vs-td>
              <vs-td>{{ c.name }}<span v-if="!c.isactive" class="fsc-muted"> (inactive)</span></vs-td>
              <vs-td class="fsc-select-cell">
                <v-select append-to-body :options="groupOptions" :reduce="o => o.value" label="label"
                  :value="mapValue(c)" placeholder="Unmapped"
                  @input="v => saveMap({ idproductcategory: c.id }, v)" />
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <template v-if="dictionary.products.length">
          <h6 class="mb-1">Products that override their category</h6>
          <p class="fsc-note mb-2">Mainly raw material (Sumal), which mixes firewood, logs and wood chips.</p>
          <vs-table :data="dictionary.products">
            <template slot="thead">
              <vs-th>Product</vs-th>
              <vs-th>Category</vs-th>
              <vs-th>Product group</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr v-for="p in data" :key="p.id">
                <vs-td>{{ p.name }}</vs-td>
                <vs-td>{{ p.category }}</vs-td>
                <vs-td class="fsc-select-cell">
                  <v-select append-to-body :options="groupOptions" :reduce="o => o.value" label="label"
                    :value="p.group_code || NOT_FSC" placeholder="Follow the category"
                    @input="v => saveMap({ idproduct: p.id }, v)" />
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </template>
      </div>
    </vx-card>

    <vs-popup class="fsc-help-popup" title="How the FSC summary works" :active.sync="helpPopup">
      <p class="fsc-help-lead">
        This page builds the annual volume summary for the FSC audit (Anexa 10) from what is already in the ERP.
        It starts from the <b>sales</b> and goes back to the raw material.
      </p>

      <h5>Where each column comes from</h5>
      <ul>
        <li><b>Raw Material Purchased</b>: receptions dated in the period whose supplier is <b>RNP Romsilva</b>: a Romsilva forest district or Direcția Silvică, recognised from the contact list, the Romsilva tax ID 1590120 or “ROMSILVA / R.N.P. / DIRECTIA SILVICA” in the name. Standing wood (APV) is already in m³; delivered wood counts when it is in m³. Communal and private forest districts are listed apart and <b>not</b> counted.</li>
        <li><b>Products Sold</b>: invoices dated in the period whose <b>quotation</b> has “FSC Required?” switched on. The ERP records FSC only on the quotation. Credit notes, invoices cancelled by a credit note, and lines that are not goods (transport, advance, discount, penalties) are left out. Volume = quantity × product volume.</li>
        <li><b>Fuel wood — Non FSC</b>: firewood sold on invoices without FSC, shown on its own row as in the 2025 document.</li>
        <li><b>Products Produced</b> = Products Sold, and <b>Finished Material On Hand</b> = 0.</li>
        <li><b>Raw Material Used</b> = sum of (sold ÷ conversion factor) over all rows.</li>
        <li><b>Raw Material In Stock</b> = the estimate you type in.</li>
      </ul>

      <h5>Product groups</h5>
      <p>
        Every product category belongs to one group of Anexa 4 (Poles W 5.5, Planks W 5.4, Fences W13.4…), set in
        “Product groups” at the bottom of the page. A product can override its category: raw material (Sumal) mixes
        firewood, logs and wood chips. A category sold on an FSC invoice without a group is shown in “Unmapped categories”
        and is not counted until you choose its group, or “Not FSC goods” for hardware and services.
        When a quotation has “FSC Required?” on, the group code is printed after each product name on the
        quotation and invoice PDFs (“… (W 5.5)”), so every document already carries the Anexa 4 nomenclature.
      </p>

      <h5>Exclusions</h5>
      <p>
        “Eucalyptus” leaves out every invoice with a line whose product, category or line description mentions eucalyptus.
        “Finger-jointed” does the same for the finger-jointed categories (decking, joists, cladding). You can add any other
        category. <b>One excluded line is enough to leave out the whole invoice</b>, both from the Anexa 10 table and from
        the invoices export, so the table always matches the exported invoices. The invoices left out are listed after the
        balance check, with the reason.
      </p>

      <h5>FSC invoices (ZIP)</h5>
      <p>
        One ZIP with the PDF of every FSC invoice of the period, exactly as it was printed (archived PDF), and an Excel file:
        the invoices, every goods line with its product group and volume, and the invoices left out by the filter.
        Advance invoices (no goods) are not part of the export.
      </p>

      <h5>Invoice PDFs</h5>
      <p>The ZIP is refused while one FSC invoice has no up-to-date PDF. “Check the PDFs” looks at every invoice:</p>
      <ul>
        <li><b>No PDF</b>: the invoice was never printed;</li>
        <li><b>Amounts changed</b>: a line was modified after the PDF was made;</li>
        <li><b>FSC mention missing</b>: the PDF does not carry the certificate number SGSCH-COC-011793;</li>
        <li><b>Not verifiable</b>: the PDF was made before the ERP recorded the printed amounts.</li>
      </ul>
      <p>
        “Generate” opens each of these invoices in the background and makes its PDF exactly like the “Invoice PDF” button,
        one after the other (keep the page open). An invoice that cannot be generated (missing address, client without
        language…) stays in the list with the reason: open it, fix it, then check again.
      </p>

      <h5>Why can there be a gap?</h5>
      <p>Purchased should equal Used + In stock. When it does not, the usual reasons are:</p>
      <ul>
        <li>a category sold as FSC has no group yet (see “Unmapped categories”);</li>
        <li>a product has no volume (see “Lines without volume”), or wood chips invoiced in tonnes although their unit says m³;</li>
        <li>an FSC order whose quotation does not have the FSC switch on;</li>
        <li>a Romsilva reception not encoded, or a Romsilva contact without the tax ID (see “Other forest districts”);</li>
        <li>a conversion factor that no longer matches the sawmill's yield.</li>
      </ul>
      <p>“Stock that would balance” is Purchased − Used. The exports always carry the real figures.</p>
    </vs-popup>
  </div>
</template>

<script>
import moment from 'moment'
import axios from '@/axios.js'
import vSelect from 'vue-select'
import { ARCHIVE_MESSAGE } from '@/mixins/invoicePdfArchive.js'

// Valeur de liste pour « rangé hors FSC » (group_code NULL côté API).
const NOT_FSC = '__not_fsc'

// Téléchargements : chemin d'API, type et nom du fichier.
const DOWNLOADS = {
  pdf: { url: '/fsc/summary/pdf', type: 'application/pdf', name: p => `FSC_Anexa10_${p.from}_${p.to}.pdf`, what: 'PDF' },
  docx: { url: '/fsc/summary/docx', type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', name: p => `FSC_Anexa10_${p.from}_${p.to}.docx`, what: 'Word' },
  zip: { url: '/fsc/invoices/zip', type: 'application/zip', name: p => `FSC_invoices_${p.from}_${p.to}.zip`, what: 'invoices ZIP' }
}

// Libellés des états de PDF (mêmes clés que api/app/services/fsc/invoicePdfCheck.js).
const PDF_STATUS_LABELS = {
  missing: 'No PDF',
  outdated: 'Amounts changed',
  no_fsc_mention: 'FSC mention missing',
  unverified: 'Not verifiable',
  error: 'Check failed'
}
// Délai maximal pour qu'un écran de facture génère et archive son PDF.
const ARCHIVE_TIMEOUT_MS = 120000

// Le filtre d'exclusion est retenu pour ce navigateur (confort seulement).
const FILTER_KEY = 'fsc.summary.exclusions'
function readSavedFilter () {
  try {
    const saved = JSON.parse(window.localStorage.getItem(FILTER_KEY) || 'null')
    if (!saved) return null
    return {
      exclude: { eucalyptus: !!(saved.exclude && saved.exclude.eucalyptus), finger_jointed: !!(saved.exclude && saved.exclude.finger_jointed) },
      excludeCats: Array.isArray(saved.excludeCats) ? saved.excludeCats.filter(n => Number.isInteger(n)) : []
    }
  } catch (e) {
    return null
  }
}

function saveBlob (data, type, filename) {
  const url = window.URL.createObjectURL(new Blob([data], { type }))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Corps JSON d'une réponse d'erreur demandée en blob (il arrive en Blob).
function blobBody (err) {
  const data = err && err.response && err.response.data
  if (!(data instanceof Blob)) return Promise.resolve(data || null)
  return data.text().then(t => {
    try { return JSON.parse(t) } catch (e) { return null }
  }).catch(() => null)
}

export default {
  name: 'FscSummary',
  components: { 'v-select': vSelect },
  data () {
    const saved = readSavedFilter()
    return {
      NOT_FSC,
      exclude: saved ? saved.exclude : { eucalyptus: false, finger_jointed: false },
      excludeCats: saved ? saved.excludeCats : [],
      from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD'),
      stock: '0',
      result: null,
      generatedWith: null,
      loading: false,
      error: null,
      helpPopup: false,
      dictOpen: false,
      dictLoaded: false,
      dictError: null,
      dictionary: { groups: [], categories: [], products: [] },
      catFilter: '',
      pdfCheck: null,
      pdfChecking: false,
      pdfRun: { running: false, cancelled: false, total: 0, done: 0, current: null },
      pdfFailures: {},
      showInactive: false
    }
  },
  computed: {
    params () {
      return {
        from: this.from,
        to: this.to,
        stock: String(this.stock === null ? '' : this.stock).trim() || '0',
        exclude: Object.keys(this.exclude).filter(k => this.exclude[k]).join(','),
        excludeCats: this.excludeCats.slice().sort((a, b) => a - b).join(',')
      }
    },
    pdfToFix () {
      return this.pdfCheck ? this.pdfCheck.invoices.filter(i => i.status !== 'ok') : []
    },
    pdfProblemCounts () {
      if (!this.pdfCheck) return []
      return Object.keys(this.pdfCheck.counts)
        .filter(status => status !== 'ok')
        .map(status => ({ status, n: this.pdfCheck.counts[status], label: PDF_STATUS_LABELS[status] || status }))
    },
    categoryOptions () {
      return this.dictionary.categories
        .filter(c => c.isactive || this.excludeCats.includes(c.id))
        .map(c => ({ value: c.id, label: c.name || `#${c.id}` }))
    },
    excludedSubtitle () {
      if (!this.result) return ''
      const n = this.result.excludedFsc.length
      const m3 = this.result.excludedFsc.reduce((a, e) => a + (Number(e.m3) || 0), 0)
      const other = this.result.excludedOtherCount
      const plus = other ? ` · plus ${other} non-FSC invoice${other === 1 ? '' : 's'}` : ''
      return `${n} FSC invoice${n === 1 ? '' : 's'} (${this.fmt(m3)} m³) not counted in the table nor exported${plus}`
    },
    stale () {
      return !!this.generatedWith && JSON.stringify(this.generatedWith) !== JSON.stringify(this.params)
    },
    balanced () {
      return this.result && Math.abs(Number(this.result.raw.gap)) < 0.005
    },
    // Au moins une ligne, vide, pour garder la forme du tableau (comme les exports).
    previewRows () {
      if (!this.result) return []
      if (this.result.rows.length) return this.result.rows
      return [{ label: '—', claim: '', conversion_factor: '', produced: '', sold: '', on_hand: '' }]
    },
    groupOptions () {
      const groups = (this.result && this.result.groups) || this.dictionary.groups
      return groups.map(g => ({ value: g.code, label: g.label }))
        .concat([{ value: NOT_FSC, label: 'Not FSC goods' }])
    },
    filteredCategories () {
      const q = this.catFilter.trim().toLowerCase()
      const matches = c => !q || String(c.name || '').toLowerCase().includes(q) || String(c.id) === q
      return this.dictionary.categories.filter(c => (this.showInactive || c.isactive) && matches(c))
    }
  },
  methods: {
    fmt (v) {
      return (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    m3 (v) {
      return `${(Number(v) || 0).toFixed(2)} m3`
    },
    dayRo (iso) {
      const [y, m, d] = String(iso || '').split('-')
      return d ? `${d}.${m}.${y}` : iso
    },
    groupLabel (code) {
      const g = this.groupOptions.find(o => o.value === code)
      return g ? g.label : code
    },
    mapValue (c) {
      if (!c.mapped) return null
      return c.group_code || NOT_FSC
    },
    validate () {
      if (!this.from || !this.to) return 'Please select both dates.'
      if (this.from > this.to) return 'The start date is after the end date.'
      const s = Number(String(this.params.stock).replace(',', '.'))
      if (!Number.isFinite(s) || s < 0) return 'The stock estimate must be a number of m³ ≥ 0.'
      return null
    },
    generate () {
      this.error = this.validate()
      if (this.error) return Promise.resolve()
      const params = Object.assign({}, this.params)
      this.loading = true
      this.$vs.loading()
      return axios.get('/fsc/summary', { params })
        .then(res => {
          this.result = res.data
          if (JSON.stringify(this.generatedWith) !== JSON.stringify(params)) {
            this.pdfCheck = null
            this.pdfFailures = {}
          }
          this.generatedWith = params
        })
        .catch(err => {
          console.error(err)
          const status = err && err.response && err.response.status
          const message = err && err.response && err.response.data && err.response.data.message
          this.error = status === 403 ? 'Access denied: admins and moderators only.' : (message || 'Could not generate the summary.')
        })
        .finally(() => {
          this.$vs.loading.close()
          this.loading = false
        })
    },
    download (kind) {
      if (this.stale) {
        this.$vs.notify({ color: 'warning', title: 'Generate first', text: 'The dates, the stock or the exclusions changed since the last generation.', position: 'bottom-right' })
        return
      }
      const params = Object.assign({}, this.generatedWith)
      const d = DOWNLOADS[kind]
      this.loading = true
      this.$vs.loading()
      axios.get(d.url, { params, responseType: 'blob' })
        .then(res => saveBlob(res.data, d.type, d.name(params)))
        .catch(err => {
          console.error(err)
          return blobBody(err).then(body => {
            const message = body && body.message
            if (body && body.check) {
              this.pdfCheck = body.check
              this.scrollToPdfCard()
            }
            this.$vs.notify({ color: 'danger', title: 'Error', text: message || `Could not generate the ${d.what} file.`, position: 'bottom-right', time: 8000 })
          })
        })
        .finally(() => {
          this.$vs.loading.close()
          this.loading = false
        })
    },
    scrollToPdfCard () {
      this.$nextTick(() => {
        const el = document.getElementById('fsc-pdf-card')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    checkPdfs () {
      if (!this.generatedWith) return Promise.resolve()
      this.pdfChecking = true
      return axios.get('/fsc/invoices/pdf-check', { params: this.generatedWith })
        .then(res => { this.pdfCheck = res.data })
        .catch(err => {
          console.error(err)
          const message = err && err.response && err.response.data && err.response.data.message
          this.$vs.notify({ color: 'danger', title: 'Error', text: message || 'Could not check the invoice PDFs.', position: 'bottom-right' })
        })
        .finally(() => { this.pdfChecking = false })
    },
    // Charge l'écran de la facture dans un cadre invisible (?archivePdf=1) : il génère le PDF
    // comme le bouton « Invoice PDF », l'archive, puis répond par postMessage.
    archiveInFrame (inv) {
      return new Promise(resolve => {
        const frame = document.createElement('iframe')
        frame.setAttribute('aria-hidden', 'true')
        frame.setAttribute('tabindex', '-1')
        frame.style.cssText = 'position:fixed;left:-12000px;top:0;width:1280px;height:900px;border:0;opacity:0;pointer-events:none'
        frame.src = this.$router.resolve({ path: `/invoice/${inv.idoperation}`, query: { archivePdf: '1' } }).href
        let timer = null
        let onMessage = null
        const done = result => {
          clearTimeout(timer)
          window.removeEventListener('message', onMessage)
          if (frame.parentNode) frame.parentNode.removeChild(frame)
          resolve(result)
        }
        onMessage = ev => {
          if (ev.origin !== window.location.origin || !ev.data || ev.data.type !== ARCHIVE_MESSAGE) return
          if (Number(ev.data.idoperation) !== Number(inv.idoperation)) return
          done({ ok: !!ev.data.ok, error: ev.data.error || null })
        }
        timer = setTimeout(() => done({ ok: false, error: 'The invoice screen did not answer within 2 minutes.' }), ARCHIVE_TIMEOUT_MS)
        window.addEventListener('message', onMessage)
        document.body.appendChild(frame)
      })
    },
    async generatePdfs () {
      const todo = this.pdfToFix.slice()
      if (!todo.length) return
      this.pdfFailures = {}
      this.pdfRun = { running: true, cancelled: false, total: todo.length, done: 0, current: null }
      for (const inv of todo) {
        if (this.pdfRun.cancelled) break
        this.pdfRun.current = inv.invoice_no || `#${inv.idoperation}`
        const result = await this.archiveInFrame(inv)
        if (!result.ok) this.$set(this.pdfFailures, inv.idoperation, result.error || 'Not generated')
        this.pdfRun.done += 1
      }
      const failed = Object.keys(this.pdfFailures).length
      const stopped = this.pdfRun.cancelled
      this.pdfRun.running = false
      await this.checkPdfs()
      this.$vs.notify({
        color: failed || stopped ? 'warning' : 'success',
        title: stopped ? 'Stopped' : 'PDFs generated',
        text: failed ? `${failed} PDF${failed === 1 ? '' : 's'} could not be generated: see the list.` : `${this.pdfRun.done} PDF${this.pdfRun.done === 1 ? '' : 's'} generated and archived.`,
        position: 'bottom-right',
        time: 8000
      })
    },
    toggleDictionary () {
      this.dictOpen = !this.dictOpen
      if (this.dictOpen && !this.dictLoaded) this.loadDictionary()
    },
    loadDictionary () {
      this.dictError = null
      return axios.get('/fsc/dictionary')
        .then(res => {
          this.dictionary = res.data
          this.dictLoaded = true
        })
        .catch(err => {
          console.error(err)
          this.dictError = (err && err.response && err.response.data && err.response.data.message) || 'Could not load the product groups.'
        })
    },
    // value : code de groupe, NOT_FSC, ou null (liste vidée = retour à « non rangé »).
    saveMap (target, value, regenerate = false) {
      const body = Object.assign({}, target)
      if (value === null || value === undefined) body.remove = true
      else body.group_code = value === NOT_FSC ? null : value
      return axios.put('/fsc/map', body)
        .then(() => {
          this.$vs.notify({ color: 'success', title: 'Saved', text: 'Product group updated.', position: 'bottom-right' })
          const jobs = []
          if (this.dictLoaded) jobs.push(this.loadDictionary())
          if (regenerate && this.result && !this.stale) jobs.push(this.generate())
          return Promise.all(jobs)
        })
        .catch(err => {
          console.error(err)
          const message = err && err.response && err.response.data && err.response.data.message
          this.$vs.notify({ color: 'danger', title: 'Error', text: message || 'Could not save the product group.', position: 'bottom-right' })
          if (this.dictLoaded) this.loadDictionary()
        })
    },
    saveFactor (group, event) {
      const raw = event && event.target ? event.target.value : event
      const factor = Number(String(raw).replace(',', '.'))
      if (!Number.isFinite(factor) || factor <= 0 || factor > 1) {
        this.$vs.notify({ color: 'danger', title: 'Invalid factor', text: 'The conversion factor must be between 0 (excluded) and 1.', position: 'bottom-right' })
        this.loadDictionary()
        return
      }
      axios.put(`/fsc/groups/${encodeURIComponent(group.code)}`, { conversion_factor: factor })
        .then(() => {
          group.conversion_factor = factor
          this.$vs.notify({ color: 'success', title: 'Saved', text: `${group.code}: conversion factor ${factor.toFixed(2)}. Generate again to apply it.`, position: 'bottom-right' })
        })
        .catch(err => {
          console.error(err)
          const message = err && err.response && err.response.data && err.response.data.message
          this.$vs.notify({ color: 'danger', title: 'Error', text: message || 'Could not save the conversion factor.', position: 'bottom-right' })
          this.loadDictionary()
        })
    }
  },
  watch: {
    params () {
      try {
        window.localStorage.setItem(FILTER_KEY, JSON.stringify({ exclude: this.exclude, excludeCats: this.excludeCats }))
      } catch (e) {
        /* stockage indisponible : le filtre ne sera simplement pas retenu */
      }
    }
  },
  created () {
    // Les catégories servent aussi au filtre d'exclusion.
    this.loadDictionary()
  },
  beforeRouteLeave (to, from, next) {
    this.helpPopup = false
    this.pdfRun.cancelled = true
    next()
  }
}
</script>

<style lang="scss">
#page-fsc-summary {
  .fsc-head h4 { margin-bottom: 2px; }
  .fsc-sub { font-size: 13px; color: #7b8794; }
  .fsc-help { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; border: 1px solid #7367f0; color: #7367f0; font-size: 13px; font-weight: 800; cursor: pointer; vertical-align: middle; user-select: none; margin-left: 4px; }
  .fsc-help:hover { background: #7367f0; color: #fff; }
  .fsc-toolbar { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 1rem; }
  .fsc-toolbar__fields { display: flex; flex-wrap: wrap; align-items: flex-end; gap: .5rem 0; }
  .fsc-toolbar__actions { display: flex; flex-wrap: wrap; align-items: center; }
  .fsc-stock { width: 240px; }
  .fsc-stale { display: flex; align-items: center; font-size: 13px; color: #ff9f43; }
  .fsc-note { font-size: 13px; color: #626262; }
  .fsc-muted { color: #9aa5b1; font-size: 12px; }
  .fsc-num { text-align: right; white-space: nowrap; }
  .fsc-nonfsc { color: #ea5455; font-weight: 600; }
  .fsc-warn-card { border-left: 4px solid #ff9f43; }
  .fsc-select-cell { min-width: 280px; }
  .fsc-select-cell .v-select { max-width: 460px; }

  .fsc-anexa-wrap { overflow-x: auto; }
  .fsc-anexa {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    th, td { border: 1px solid #c8ccd0; padding: 6px 8px; text-align: center; vertical-align: middle; }
    th { background: #f5f5f7; font-weight: 600; }
    th small { font-weight: 400; color: #7b8794; }
  }

  .fsc-balance { display: flex; flex-wrap: wrap; align-items: center; gap: .75rem; }
  .fsc-balance__item { display: flex; flex-direction: column; padding: 8px 14px; border-radius: 8px; background: #f5f5f7; min-width: 150px; }
  .fsc-balance__item.is-ok { background: rgba(40, 199, 111, .12); color: #1f9d57; }
  .fsc-balance__item.is-gap { background: rgba(234, 84, 85, .12); color: #ea5455; }
  .fsc-balance__label { font-size: 12px; opacity: .8; }
  .fsc-balance__value { font-size: 18px; font-weight: 700; }
  .fsc-balance__op { font-size: 20px; font-weight: 700; color: #9aa5b1; }

  .fsc-dict-head { display: flex; align-items: center; justify-content: space-between; cursor: pointer; }
  .fsc-dict-filter { display: flex; flex-wrap: wrap; align-items: center; }
  .fsc-filters { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem 0; }
  .fsc-pdf-actions { display: flex; flex-wrap: wrap; align-items: center; }
  .fsc-pdf-summary { display: flex; flex-wrap: wrap; align-items: center; }
  .fsc-pdf-summary .con-vs-chip { float: none; margin-right: .5rem; margin-bottom: .25rem; }
  .fsc-filters__label { font-weight: 600; margin-right: .75rem; }
  .fsc-filters__cats { min-width: 320px; max-width: 640px; flex: 1; }
  .fsc-factor { width: 110px; }
}

.fsc-help-popup {
  h5 { font-size: 14px; font-weight: 700; margin: 18px 0 8px; }
  p, li { font-size: 13px; color: #445; line-height: 1.55; }
  ul { padding-left: 18px; margin: 4px 0 8px; list-style: disc; }
  li { margin-bottom: 4px; }
  .fsc-help-lead { font-size: 14px; color: #556; }
}
</style>
